import React, { useEffect, useState } from 'react';
import {
  TrendingUp, ShoppingCart, Package, Users, Truck, Heart,
  UserCheck, Pill, AlertTriangle, Calendar, DollarSign, TrendingDown, BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { dashboardService } from '../api/services';
import { StatCard } from '../components/UI';
import { formatCurrency, formatNumber } from '../utils/helpers';
import { toJalali } from '../utils/jalali';

export default function Dashboard() {
  const [stats, setStats]     = useState(null);
  const [chartData, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sR, cR] = await Promise.all([dashboardService.getStats(), dashboardService.getSalesChart()]);
        setStats(sR.data.data);
        setChart((cR.data.data||[]).map(d => ({ ...d, date: d._id, profit: d.total - (d.cogs||0) })));
      } catch(e) { setError(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 font-medium">Dashboard Error</p>
      <p className="text-red-400 text-sm mt-1">{error}</p>
      <button onClick={()=>window.location.reload()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Retry</button>
    </div>
  );

  const s = stats || {};

  // Profit margin %
  const profitMargin = s.totalSales > 0 ? ((s.grossProfit / s.totalSales) * 100).toFixed(1) : 0;

  const salesCards = stats ? [
    { title:'Total Revenue',     value:formatCurrency(s.totalSales),     icon:TrendingUp,   color:'green',  sub:`Daily: ${formatCurrency(s.dailySales)}` },
    { title:'Total COGS',        value:formatCurrency(s.totalCOGS||0),   icon:ShoppingCart, color:'orange', sub:'Cost of goods sold' },
    { title:'Gross Profit',      value:formatCurrency(s.grossProfit||0), icon:BarChart3,    color:'blue',   sub:`Margin: ${profitMargin}%` },
    { title:'Expenses',          value:formatCurrency(s.expenses||0),    icon:TrendingDown, color:'red',    sub:'Total recorded expenses' },
    { title:'Net Profit',        value:formatCurrency(s.netProfit||0),   icon:DollarSign,   color:(s.netProfit||0)>=0?'green':'red', sub:'Revenue - COGS - Expenses' },
    { title:'Monthly Sales',     value:formatCurrency(s.monthlySales),   icon:TrendingUp,   color:'purple', sub:`Profit: ${formatCurrency(s.monthlyProfit||0)}` },
    { title:'Customers',         value:formatNumber(s.totalCustomers),   icon:Users,        color:'blue',   sub:`${s.totalDebtors||0} with debt` },
    { title:'Suppliers',         value:formatNumber(s.totalSuppliers),   icon:Truck,        color:'orange', sub:`${s.totalCreditors||0} with debt` },
    { title:'Employees',         value:formatNumber(s.totalEmployees||0),icon:UserCheck,    color:'blue' },
    { title:'Medicines',         value:formatNumber(s.totalMedicines),   icon:Pill,         color:'purple' },
  ] : [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">{toJalali(new Date())} — Welcome back!</p>
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {Array(10).fill(0).map((_,i)=><div key={i} className="bg-white rounded-xl border border-gray-200 p-5 h-24 animate-pulse"><div className="h-3 bg-gray-100 rounded w-20 mb-3"/><div className="h-6 bg-gray-100 rounded w-24"/></div>)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {salesCards.map((card,i)=><StatCard key={i} {...card}/>)}
        </div>
      )}

      {/* Net Profit highlight */}
      {stats && (
        <div className={`rounded-xl border-2 p-5 mb-6 flex items-center justify-between ${(s.netProfit||0)>=0?'border-green-200 bg-green-50':'border-red-200 bg-red-50'}`}>
          <div>
            <p className="text-sm text-gray-500 mb-1">Net Profit / Loss (All Time)</p>
            <p className={`text-3xl font-bold ${(s.netProfit||0)>=0?'text-green-700':'text-red-700'}`}>{formatCurrency(s.netProfit||0)}</p>
          </div>
          <div className="text-right text-sm space-y-1">
            <div className="flex justify-between gap-8"><span className="text-gray-500">Revenue</span><span className="text-green-700 font-medium">{formatCurrency(s.totalSales)}</span></div>
            <div className="flex justify-between gap-8"><span className="text-gray-500">COGS</span><span className="text-orange-600">- {formatCurrency(s.totalCOGS||0)}</span></div>
            <div className="flex justify-between gap-8"><span className="text-gray-500">Expenses</span><span className="text-red-500">- {formatCurrency(s.expenses||0)}</span></div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-4">Sales & Profit Trend (30 Days)</p>
          {chartData.length===0&&!loading ? <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data yet</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{fontSize:10}} tickLine={false}/>
                <YAxis tick={{fontSize:10}} tickLine={false} axisLine={false}/>
                <Tooltip formatter={v=>formatCurrency(v)}/>
                <Legend/>
                <Line type="monotone" dataKey="total"  name="Revenue" stroke="#2563eb" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="profit" name="Profit"  stroke="#16a34a" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-4">Daily Transactions</p>
          {chartData.length===0&&!loading ? <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data yet</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{fontSize:10}} tickLine={false}/>
                <YAxis tick={{fontSize:10}} tickLine={false} axisLine={false}/>
                <Tooltip/>
                <Bar dataKey="count" name="Transactions" fill="#3b82f6" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Alerts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 p-4 border-b border-gray-100">
              <AlertTriangle size={16} className="text-orange-500"/>
              <h3 className="text-sm font-semibold text-gray-700">Low Stock Alerts</h3>
              <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{s.lowStockItems?.length||0}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {!s.lowStockItems?.length ? <p className="text-sm text-gray-400 text-center py-8">All stock levels healthy ✓</p> :
                s.lowStockItems.slice(0,6).map((item,i)=>(
                  <div key={i} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-sm font-medium text-gray-800">{item.medicine?.commercialName||'N/A'}</p><p className="text-xs text-gray-400">{item.medicine?.genericName}</p></div>
                    <div className="text-right"><span className="text-sm font-bold text-orange-600">{item.currentStock}</span><p className="text-xs text-gray-400">/ {item.medicine?.reorderLevel||10} min</p></div>
                  </div>
                ))
              }
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 p-4 border-b border-gray-100">
              <Calendar size={16} className="text-red-500"/>
              <h3 className="text-sm font-semibold text-gray-700">Expiring Soon (90 days)</h3>
              <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{s.expiringItems?.length||0}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {!s.expiringItems?.length ? <p className="text-sm text-gray-400 text-center py-8">No medicines expiring soon ✓</p> :
                s.expiringItems.slice(0,6).map((item,i)=>(
                  <div key={i} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-sm font-medium text-gray-800">{item.medicine?.commercialName||'N/A'}</p><p className="text-xs text-gray-400">Batch: {item.batchNumber||'-'}</p></div>
                    <div className="text-right"><span className="text-sm font-medium text-red-600">{toJalali(item.expiryDate)}</span><p className="text-xs text-gray-400">{item.currentStock} units</p></div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
